<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useContactFilterContext } from './contactProvider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';
import VisibilitySelector from './VisibilitySelector.vue';
import SegmentKanbanBlock from './SegmentKanbanBlock.vue';
import SegmentPreview from './SegmentPreview.vue';
import { buildSegmentQuery, isBlankCondition } from './helper/segmentQuery';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  isSegmentView: { type: Boolean, default: false },
  segmentName: { type: String, default: '' },
  segmentVisibility: { type: String, default: 'personal' },
});

const emit = defineEmits([
  'applyFilter',
  'updateSegment',
  'close',
  'clearFilters',
]);
const { attributeFilterTypes } = useContactFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
// Bloco do funil do segmento (spec 007); nulo = sem bloco.
const kanbanBlock = defineModel('kanbanBlock', {
  type: Object,
  default: null,
});
const segmentNameLocal = ref(props.segmentName);
const segmentVisibilityLocal = ref(props.segmentVisibility);
const currentRole = useMapGetter('getCurrentRole');
const isAdmin = computed(() => currentRole.value === 'administrator');
const accountId = useMapGetter('getCurrentAccountId');
const isModuleEnabledonAccount = useMapGetter(
  'accounts/isModuleEnabledonAccount'
);
const canUseKanbanBlock = computed(
  () =>
    isAdmin.value &&
    isModuleEnabledonAccount.value(accountId.value, 'kanban') === true
);

const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const { t } = useI18n();
const store = useStore();

const resetFilter = () => {
  emit('clearFilters');
  filters.value = [{ ...DEFAULT_FILTER }];
};

const clearAllFilters = () => {
  kanbanBlock.value = null;
  resetFilter();
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

// Com o bloco do funil, a linha padrao sem valor nao conta: o segmento pode ser so "tem cartao no funil".
const contactConditions = computed(() =>
  kanbanBlock.value && filters.value.every(isBlankCondition)
    ? []
    : filters.value
);
const isKanbanBlockValid = computed(
  () => !kanbanBlock.value || !!kanbanBlock.value.board_id
);

const isConditionsValid = () => {
  if (!isKanbanBlockValid.value) return false;
  if (!contactConditions.value.length) return true;
  return conditionsRef.value.every(condition => condition.validate());
};

const previewQuery = computed(() => {
  if (!isKanbanBlockValid.value) return null;
  if (contactConditions.value.some(isBlankCondition)) return null;
  return buildSegmentQuery(
    useSnakeCase(JSON.parse(JSON.stringify(contactConditions.value))),
    kanbanBlock.value
  );
});

const updateSavedSegment = () => {
  if (isConditionsValid()) {
    emit(
      'updateSegment',
      contactConditions.value,
      segmentNameLocal.value,
      segmentVisibilityLocal.value,
      kanbanBlock.value
    );
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) return;

  store.dispatch(
    'contacts/setContactFilters',
    useSnakeCase(JSON.parse(JSON.stringify(contactConditions.value)))
  );
  store.dispatch('contacts/setContactKanbanBlock', kanbanBlock.value);
  emit('applyFilter', contactConditions.value, kanbanBlock.value);
  useTrack(CONTACTS_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isSegmentView
    ? t('CONTACTS_LAYOUT.FILTER.TITLE')
    : t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleContactsFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 w-[min(34rem,calc(100vw-2rem))] lg:w-[750px] overflow-visible border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div v-if="props.isSegmentView">
      <div class="pb-6 border-b border-n-weak grid gap-4">
        <Input
          v-model="segmentNameLocal"
          :label="$t('CONTACTS_LAYOUT.FILTER.SEGMENT.LABEL')"
          :placeholder="t('CONTACTS_LAYOUT.FILTER.SEGMENT.INPUT_PLACEHOLDER')"
        />
        <VisibilitySelector
          v-if="isAdmin"
          v-model="segmentVisibilityLocal"
          i18n-prefix="CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY"
        />
      </div>
    </div>
    <ul class="grid gap-4 list-none min-w-0">
      <template v-for="(filter, index) in filters" :key="filter.id">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="attributeFilterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="attributeFilterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <SegmentKanbanBlock v-if="canUseKanbanBlock" v-model="kanbanBlock" />
    <SegmentPreview :query="previewQuery" />
    <div class="flex justify-between gap-2">
      <Button sm ghost blue class="flex-shrink-0" @click="addFilter">
        {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_FILTER') }}
      </Button>
      <div class="flex gap-2 flex-shrink-0">
        <Button sm faded slate @click="clearAllFilters">
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.CLEAR_FILTERS') }}
        </Button>
        <Button
          v-if="isSegmentView"
          sm
          solid
          blue
          :disabled="!segmentNameLocal"
          @click="updateSavedSegment"
        >
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.UPDATE_SEGMENT') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.APPLY_FILTERS') }}
        </Button>
      </div>
    </div>
  </div>
</template>
